import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "../components/admin/AdminShell";
import PushHealthPanel from "../components/admin/PushHealthPanel";
import { Button } from "../components/ui/button";
import {
  RefreshCw, Loader2, BellRing, BellOff, ShieldAlert, KeyRound,
  AlertTriangle, CheckCircle2, HelpCircle,
} from "lucide-react";

const API = "/api";

const CAUSE_STYLE = {
  operational: { icon: CheckCircle2, cls: "text-emerald-300", ring: "ring-emerald-400/30", bg: "bg-emerald-500/10" },
  delivery_unverified: { icon: HelpCircle, cls: "text-amber-300", ring: "ring-amber-400/30", bg: "bg-amber-500/10" },
  no_device: { icon: BellOff, cls: "text-slate-300", ring: "ring-white/15", bg: "bg-white/5" },
  permission_denied: { icon: ShieldAlert, cls: "text-orange-300", ring: "ring-orange-400/30", bg: "bg-orange-500/10" },
  invalid_token: { icon: AlertTriangle, cls: "text-rose-300", ring: "ring-rose-400/30", bg: "bg-rose-500/10" },
  provider_failure: { icon: AlertTriangle, cls: "text-rose-300", ring: "ring-rose-400/30", bg: "bg-rose-500/10" },
  blocked_by_credentials: { icon: KeyRound, cls: "text-sky-300", ring: "ring-sky-400/30", bg: "bg-sky-500/10" },
};

export default function AdminPushHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideTest, setHideTest] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/push-health/diagnosis`, {
        params: hideTest ? { hide_test: "1" } : {},
      });
      setData(r.data);
    } catch (_e) {
      toast.error("No se pudo cargar el diagnóstico de notificaciones");
    } finally {
      setLoading(false);
    }
  }, [hideTest]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminShell
      testid="admin-push-health"
      title="Salud de notificaciones"
      description="Diagnóstico honesto por causa real: sin dispositivo, permiso denegado, token inválido, fallo del proveedor o entrega sin confirmar."
      actions={
        <>
          <label className="flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-white/15 bg-[#0B2545] px-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={hideTest}
              onChange={(e) => setHideTest(e.target.checked)}
              className="accent-[#1B84FF]"
              data-testid="push-health-hide-test"
            />
            Ocultar datos de prueba
          </label>
          <Button variant="outline" onClick={load} className="border-white/15 text-slate-300" data-testid="push-health-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          </Button>
        </>
      }
    >
      {loading && !data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5BC0FF]" /></div>
      ) : (
        <div className="space-y-8">
          <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="push-health-honesty">
            {data?.honesty_note}
          </div>

          <section>
            <h2 className="mb-4 text-base font-semibold text-white md:text-lg">Cuentas por causa</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="push-health-causes">
              {(data?.causes || []).map((c) => {
                const st = CAUSE_STYLE[c.cause] || CAUSE_STYLE.no_device;
                const Icon = st.icon;
                return (
                  <div
                    key={c.cause}
                    className={`rounded-xl p-4 ring-1 ring-inset ${st.ring} ${st.bg}`}
                    data-testid={`push-cause-${c.cause}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <Icon className={`h-5 w-5 ${st.cls}`} aria-hidden="true" />
                      <span className="text-2xl font-bold text-white">{c.accounts}</span>
                    </div>
                    <p className={`mt-3 text-sm font-medium ${st.cls}`}>{c.label}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-base font-semibold text-white md:text-lg">Proveedores de envío</h2>
            <div className="grid gap-3 sm:grid-cols-3" data-testid="push-health-providers">
              {(data?.providers || []).map((p) => (
                <div key={p.provider} className="rounded-xl border border-white/10 bg-[#0B2545] p-4">
                  <div className="flex items-center gap-2">
                    <BellRing className={`h-4 w-4 ${p.enabled ? "text-emerald-300" : "text-slate-500"}`} aria-hidden="true" />
                    <p className="font-medium text-white">{p.provider}</p>
                  </div>
                  <p className={`mt-2 text-xs font-bold uppercase tracking-wide ${p.enabled ? "text-emerald-300" : "text-amber-300"}`}>
                    {p.enabled ? "Configurado" : "Bloqueado por credenciales"}
                  </p>
                  <p className="mt-2 text-xs text-slate-400">{p.note}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-base font-semibold text-white md:text-lg">
              Clientes que pagan y no reciben avisos
            </h2>
            <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#0B2545]">
              {(data?.affected_paying_accounts || []).length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400" data-testid="push-health-affected-empty">
                  Ningún cliente con membresía activa tiene un problema de notificaciones.
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="push-health-affected-table">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="p-3">Cliente</th>
                      <th className="p-3">Causa exacta</th>
                      <th className="p-3">Dispositivos</th>
                      <th className="p-3">Origen</th>
                      <th className="p-3">Vence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.affected_paying_accounts.map((r) => (
                      <tr key={r.user_id} className="border-b border-white/5" data-testid={`push-affected-${r.user_id}`}>
                        <td className="p-3">
                          <p className="font-medium text-white">{r.full_name}</p>
                          <p className="text-xs text-slate-500">{r.email}</p>
                        </td>
                        <td className="p-3 text-slate-300">{r.cause_label}</td>
                        <td className="p-3 text-slate-300">{r.devices}</td>
                        <td className="p-3 text-slate-400">{r.registration_source}</td>
                        <td className="p-3 text-slate-400">
                          {r.membership_expires_at ? new Date(r.membership_expires_at).toLocaleDateString("es-PE") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-base font-semibold text-white md:text-lg">Panel operativo detallado</h2>
            <PushHealthPanel />
          </section>
        </div>
      )}
    </AdminShell>
  );
}
